import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger,
} from './ui/dialog';
import { AlertTriangle, Trash2 } from 'lucide-react';

/**
 * WipeInvestorButton — single-investor hard-wipe.
 *
 * Deletes (for ONE investor only):
 *   • investor_transactions where source ∈ {csv_import, investec_historical}
 *   • transaction_allocations bound to those bank rows
 *   • auto_match_audit rows
 *   • csv_import_batches headers
 *
 * Preserves: expected_cashflows (Excel RP/LB), live investec_sync rows,
 * and every other investor's data.
 *
 * Requires the admin to type "WIPE-{investor_id}" exactly to confirm.
 */
export function WipeInvestorButton({ API_URL, headers, investorId, onComplete }) {
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);

  const required = investorId ? `WIPE-${investorId}` : '';
  const ready = required && confirmText === required;

  const handleConfirm = async () => {
    if (!ready) return;
    setBusy(true);
    try {
      const r = await axios.post(
        `${API_URL}/api/admin/investor-transactions/wipe-investor`,
        { investor_id: investorId, confirm: required },
        { headers }
      );
      toast.success(r.data?.message || `Wiped ${investorId}.`);
      setOpen(false);
      setConfirmText('');
      onComplete && onComplete(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Wipe failed');
    } finally {
      setBusy(false);
    }
  };

  const disabled = !investorId || investorId === 'all' || investorId === '';

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setConfirmText(''); }}>
      <DialogTrigger asChild>
        <Button
          size="sm"
          variant="destructive"
          disabled={disabled}
          data-testid="wipe-investor-button"
          title={disabled ? 'Select a single investor first' : `Wipe ${investorId}'s CSV + historical bank data`}
        >
          <Trash2 className="h-3.5 w-3.5 mr-1" />
          Wipe Investor Data
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="wipe-investor-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <AlertTriangle className="h-5 w-5" /> Wipe Investor Transactions
          </DialogTitle>
          <DialogDescription className="space-y-3 pt-3">
            <span className="block">
              This will <strong>hard-delete</strong> EVERY investor
              transaction row for{' '}
              <strong className="font-mono">{investorId}</strong> — CSV
              imports, historical bank, live Investec-synced bank rows,
              staging rows, allocations and audit log. <strong>This
              cannot be undone.</strong>
            </span>
            <span className="block text-sm">
              <strong>Preserved:</strong> `expected_cashflows` (loanbook
              RP/LB Excel data), and every other investor. Re-sync from
              Investec after wipe to pull live bank rows back in.
            </span>
            <span className="block text-sm">
              To confirm, type{' '}
              <code className="bg-slate-100 px-1.5 py-0.5 rounded text-red-700">{required}</code>{' '}
              into the box below:
            </span>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-2">
          <Input
            placeholder={required}
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            className={`font-mono ${ready ? 'border-red-600' : ''}`}
            data-testid="wipe-investor-confirm-input"
            autoFocus
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={!ready || busy}
            onClick={handleConfirm}
            data-testid="wipe-investor-confirm-button"
          >
            {busy ? 'Wiping…' : `Wipe ${investorId}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
